import { Divider, EmptySearchResult, LegacyCard, Spinner, Tabs } from "@shopify/polaris"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import settingsApi from "@/api/settings"
import { CourierMatchingTable } from "@/component"
import CardTitle from "@/component/TitleGroup/CardTitle"
import CourierDraggableTable from "@/pages/Settings/components/CourierMatching/CourierDraggableTable"
import toast from "@/utils/toast"

import styles from "../../Settings.module.scss"

type courierType = "enable" | "disable"

export type CourierType = {
  express: string
  img: string
  name: string
  sort: number
  checked?: boolean;
}

type CouriersType = {
  enable: CourierType[]
  disable: CourierType[]
}

export default function CourierMatching () {
  const { t } = useTranslation(["settings", "common"])
  const [loading, setLoading] = useState(true)
  const [tabIndex, setTabIndex] = useState(0)
  const [couriers, setCouriers] = useState<CouriersType>({ enable: [], disable: [] })
  const [selectedCouriers, setSelectedCouriers] = useState<string[]>([])
  const [queryValue, setQueryValue] = useState("")
  const courierTabs:{id: courierType, content: string}[] = [
    { id: "enable", content: t("settings:FrequentlyUsedCarriers") },
    { id: "disable", content: t("settings:CarrierList") },
  ]

  useEffect(() => {
    // 初始化couriers
    setLoading(true)
    settingsApi.getCouriersList()
      .then(({ data: { couriers: { enable, disable } } }) => {
        setCouriers({ enable, disable })

      })
      .finally(() => setLoading(false))
  }, [])

  // 排序
  const handleCourierSortChange = (enableList: CourierType[]) => {
    settingsApi.sortEnable(enableList.map(item => item.express))
      .then((res) => {
        const { enable } = res.data.couriers

        toast(t("settings:CourierListUpdated"), 1500)
        setCouriers({ enable: enable, disable: couriers.disable })
      })
    setCouriers({ enable: enableList, disable: couriers.disable })
  }

  // 禁用
  const handleCourierDisable = (couriers: string[]) => {
    setLoading(true)
    settingsApi.setDisable(couriers).then(({ data: { couriers: { enable, disable } } }) => {
      setCouriers({ enable, disable })
      setSelectedCouriers([])
      toast(t("settings:CourierListUpdated"), 1500)
    })
      .finally(() => {
        setLoading(false)
      })
  }

  // 启用
  const handleCourierEnable = (couriers: string[]) => {
    setLoading(true)
    settingsApi.setEnable(couriers).then(({ data: { couriers: { enable, disable } } }) => {
      setCouriers({ enable, disable })
      setSelectedCouriers([])
      toast(t("settings:CourierListUpdated"), 1500)
    })
      .finally(() => {
        setLoading(false)
      })
  }

  // Tab 切换
  const handleCourierTabSwitch = (index: number) => {
    setTabIndex(index)
    setQueryValue("")
    setSelectedCouriers([])
  }

  const emptyStateMarkup = tabIndex === 0 ? (
    <EmptySearchResult
      title={t("settings:CourierMatching.EmptySearchResult.title")}
      description={t("settings:EmptyUsedCarrier")}
      withIllustration
    />
  ) : (
    // 没有 description 时会多出一行 20px空白，需要利用上边距补上
    <div className="mt-4">
      <EmptySearchResult
        title={t("settings:CourierMatching.EmptySearchResult.title")}
        withIllustration
      />
    </div>
  )

  const activeType = courierTabs[tabIndex].id

  return <div id="CourierMatching" className={`CourierMatching${tabIndex}`}>
    <LegacyCard title={<CardTitle title={t("settings:FrequentlyUsedCarriers")}
      description={t("settings:FrequentlyUsedCarriersDesc")}
    />}
    >
      <div className={"mt-2 mb-1"}><Divider /></div>

      <Tabs tabs={courierTabs} selected={tabIndex} onSelect={handleCourierTabSwitch} />

      <div className={"mt-1"}><Divider /></div>

      {
        loading && <div className={styles.loadingMask}>
          <div className={styles.spinnerWrapper}>
            <Spinner hasFocusableParent={false} />
          </div>
        </div>
      }

      {/* Enable列表 分两个列表的原因是Polaris ResourceList组件不支持拖拽功能, 使用block/hidden防止重新渲染，屏幕抖动 */}
      <div className={activeType === "enable" ? "block" : "hidden"}>
        <CourierDraggableTable
          items={couriers.enable}
          queryValue={queryValue}
          onQueryChange={setQueryValue}
          selections={selectedCouriers}
          onSelectionChange={setSelectedCouriers}
          onSortChange={handleCourierSortChange}
          onDisabled={() => handleCourierDisable(selectedCouriers)}
          emptyState={<div className={"pt-4 pb-8"}>{emptyStateMarkup}</div>}
        />
      </div>

      {/* Disabled列表 */}
      <div className={activeType === "disable" ? "block" : "hidden"}>
        <CourierMatchingTable
          emptyState={<div className={"pt-4 pb-8"}>{emptyStateMarkup}</div>}
          items={couriers.disable}
          // arrowSortable={activeType === "enable"}
          enableButton={activeType === "disable"}
          perPage={20}
          selectedCouriers={selectedCouriers}
          idForItem={(item: any) => item.express}
          onDisable={handleCourierDisable}
          onEnable={handleCourierEnable}
          onSelectionChange={setSelectedCouriers}
          queryValue={queryValue}
          onQueryChange={setQueryValue}
        />
      </div>
    </LegacyCard>
  </div>
}
